import { c as _c } from "react/compiler-runtime";
import { DialogAction } from "@catchy/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MenuItem } from "@headlessui/react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";
import { applyDepartureBoardFreshness, nextDeparturePredictionExpiry } from "@catchy/app-core/src/departureBoardFreshness";
import { Virtuoso } from "react-virtuoso";
import { db } from "@/lib/database";
import { feedInfo as feed_info } from "@/schemas/gtfs";
import checkStopType from "@/lib/stopTypeChecker";
import { getCanonicalSiteUrl } from "@/lib/regions/client";
import { getStopTypeDetails } from "@/lib/transitDisplay";
import { getActiveWellingtonStopAlerts } from "@/lib/server/metlink/serviceAlertsDatabase";
import { getStopPageData } from "@/lib/server/stops/getStopPageData";
import { getNearbyStops } from "@/lib/server/stops/getNearbyStops";
import { confirmedNotFound } from "@catchy/app-core/src/isrSafety";
import { getDisplayPlatformKey, getDirectionControlLabelsFromStop, getStopDepartureEmptyState, formatVehicleIdentity, getServiceDisplayName, useTrackRecentStop, getPublicStopIdentifier, getStoredStopId, shouldShowDirectionPicker, withStoredStopId, shouldShowDoubleDeckerIcon, shouldShowElectricTransportIcon } from "@catchy/app-core";
import useLocalStorage from "@/hooks/useLocalStorage";
import { school_bus_schools } from "@/data/school_bus_schools";
import { Alert, BackLink, DestinationStopSelector, DirectionControl, FavouriteButton, Modal, OptionsMenu, WeatherWidget } from "@catchy/ui";
import RouteBadge from "@/components/RouteBadge";
import CloseCross from "@catchy/assets/svgs/close-cross.svg";
import DestinationFlag from "@catchy/assets/svgs/destination-flag.svg";
import Spinner from "@catchy/assets/svgs/spinner.svg";
import StopMarker from "@catchy/assets/svgs/stop-mono-lg.svg";
import BusElectric from "@catchy/assets/svgs/bus-electric.svg";
import BusDoubleDecker from "@catchy/assets/svgs/bus-double-decker.svg";
import ChevronLeft from "@catchy/assets/svgs/chevron-left.svg";
import ChevronRight from "@catchy/assets/svgs/chevron-right.svg";
import ChevronDownSmall from "@catchy/assets/svgs/chevron-down-sm.svg";
const typeTranslator = {
  bus: "Bus",
  train: "Train",
  cable_car: "Cable Car",
  ferry: "Ferry"
};
const siteUrl = getCanonicalSiteUrl();
const RECENT_DEPARTURE_WINDOW_MS = 5 * 60 * 1000;
const relativeMinuteFormatter = new Intl.RelativeTimeFormat("en-AU", {
  numeric: "always",
  style: "short"
});
function getStopSpinnerStyle(stopType) {
  return {
    color: getStopTypeDetails(stopType).color
  };
}
const getRelativeDate = date => {
  const rtf = new Intl.RelativeTimeFormat("en", {
    numeric: "auto"
  });
  const now = new Date().toLocaleString("en-CA", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const targetDate = date.toLocaleString("en-CA", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const nowDate = new Date(`${now}T00:00:00`);
  const targetDateObj = new Date(`${targetDate}T00:00:00`);
  const diffInMs = targetDateObj - nowDate;
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));
  return rtf.format(diffInDays, "day");
};
const formatRelativeMinutesAgo = seconds => relativeMinuteFormatter.format(-Math.round(Math.abs(seconds) / 60), "minute");
const formatRelativeMinutes = seconds => relativeMinuteFormatter.format(Math.round(seconds / 60), "minute").replace(/^in\s+/i, "");
const fetcher = async url => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error ${response.status} for ${url}`);
  }
  return response.json();
};
const getRouteDetails = (id, routes) => {
  const match = routes.find(route => route.route_id === id);
  return match && {
    route_color: match.route_color,
    route_type: match.route_type,
    routeLabel: match.route_short_name
  };
};
const formatNzDateKey = dateValue => new Date(dateValue).toLocaleString("en-CA", {
  timeZone: "Pacific/Auckland",
  year: "numeric",
  month: "2-digit",
  day: "2-digit"
});
const groupByDepartureDate = (objectArray, dateKeys) => {
  return objectArray.reduce((acc, obj) => {
    const dateValue = dateKeys.reduce((chosenDate, key) => chosenDate || obj[key], null);
    if (!dateValue) return acc;
    const dateKey = formatNzDateKey(dateValue);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(obj);
    return acc;
  }, {});
};
export async function getStaticProps(context) {
  const stopId = context.params.stop_id;

  // Helper to fetch GTFS feed start/end dates
  async function getFeedDateRange() {
    try {
      const results = await db.select({
        start_date: feed_info.feed_start_date,
        end_date: feed_info.feed_end_date
      }).from(feed_info).limit(1);
      if (!results[0]) return null;
      const normalizeDate = value => {
        if (!value) return null;

        // Drizzle/PG may return DATE columns as strings ("YYYY-MM-DD") or Date objects
        if (value instanceof Date) return value.toISOString().slice(0, 10);
        if (typeof value === "string") {
          // If it somehow arrives as GTFS-style YYYYMMDD, normalize it
          if (/^\d{8}$/.test(value)) {
            return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
          }

          // Otherwise assume it's already ISO-ish; keep just YYYY-MM-DD
          return value.slice(0, 10);
        }
        return null;
      };
      return {
        start_date: normalizeDate(results[0].start_date),
        end_date: normalizeDate(results[0].end_date)
      };
    } catch (error) {
      console.error("Error fetching feed info:", error);
      return null;
    }
  }
  try {
    const stopData = await getStopPageData(stopId);
    if (!stopData) {
      return confirmedNotFound();
    }
    const stopType = checkStopType(stopId);
    const {
      routes: servicesAtStop,
      ...stop
    } = stopData;
    const [alertsAtStop, feedInfo, nearbyStops] = await Promise.all([getActiveWellingtonStopAlerts(stop.stop_id).catch(error => {
      console.error(`Error fetching alerts for stop ${stop.stop_id}:`, error);
      return [];
    }), getFeedDateRange(), getNearbyStops(stop.stop_id, stop.stop_lat, stop.stop_lon)]);
    return {
      props: {
        stop,
        stopType,
        stopId,
        servicesAtStop,
        alertsAtStop: alertsAtStop ?? [],
        feedInfo,
        nearbyStops
      },
      revalidate: 3000
    };
  } catch (error) {
    console.error("Error in getStaticProps:", error);
    throw error;
  }
}
export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking"
  };
}
const PageWrapper = t0 => {
  const $ = _c(72);
  const {
    children,
    nearbyStops: t1,
    preferredStopName,
    servicesAtStop: t2,
    stop,
    stopType
  } = t0;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== preferredStopName || $[1] !== stop || $[2] !== stopType || $[3] !== t1 || $[4] !== t2) {
    const nearbyStops = t1 === undefined ? [] : t1;
    const servicesAtStop = t2 === undefined ? [] : t2;
    const stopName = preferredStopName || stop.stop_name;
    const publicStopIdentifier = getPublicStopIdentifier(stop);
    const stopLabel = `${stopName}${stopType === "bus" ? " Bus Stop" : ""}`;
    const title = `${stopLabel} departures and timetable — Catchy`;
    const t19 = `${typeTranslator[stopType]} departures and timetable for ${stopLabel} in Wellington`;
    const t20 = `Stop ${publicStopIdentifier}`;
    const t21 = servicesAtStop.length ? `${servicesAtStop.length} service${servicesAtStop.length === 1 ? "" : "s"} stop here` : null;
    let t22;
    if ($[22] !== t19 || $[23] !== t20 || $[24] !== t21) {
      t22 = [t19, t20, t21].filter(Boolean);
      $[22] = t19;
      $[23] = t20;
      $[24] = t21;
      $[25] = t22;
    } else {
      t22 = $[25];
    }
    const description = t22.join(". ");
    const shareImage = `${siteUrl}/api/ogimage/stop/${encodeURIComponent(stop?.stop_id)}`;
    const canonicalUrl = `${siteUrl}/stop/${stop?.stop_id}`;
    const jsonLd = {
      "@context": "https://schema.org",
      "@graph": [{
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        description,
        isPartOf: {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          name: "Catchy",
          url: siteUrl
        },
        breadcrumb: {
          "@id": `${canonicalUrl}#breadcrumb`
        }
      }, {
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        itemListElement: [{
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl
        }, {
          "@type": "ListItem",
          position: 2,
          name: "Stop",
          item: canonicalUrl
        }]
      }, {
        "@type": "Place",
        "@id": `${canonicalUrl}#place`,
        name: stopLabel,
        description,
        url: canonicalUrl,
        identifier: publicStopIdentifier,
        geo: {
          "@type": "GeoCoordinates",
          latitude: stop.stop_lat,
          longitude: stop.stop_lon
        }
      }, ...(servicesAtStop.length ? [{
        "@type": "ItemList",
        "@id": `${canonicalUrl}#services`,
        name: `Services at ${stopLabel}`,
        itemListElement: servicesAtStop.slice(0, 12).map(_temp)
      }] : []), ...(nearbyStops.length ? [{
        "@type": "ItemList",
        "@id": `${canonicalUrl}#nearby-stops`,
        name: `Nearby stops to ${stopLabel}`,
        itemListElement: nearbyStops.map(_temp2)
      }] : [])]
    };
    t18 = "pb-20";
    T0 = Head;
    if ($[26] !== title) {
      t6 = <title key="title">{title}</title>;
      $[26] = title;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <meta name="robots" content="index,follow,max-image-preview:large" />;
      $[28] = t7;
    } else {
      t7 = $[28];
    }
    if ($[29] !== description) {
      t8 = <meta name="description" content={description} key="description" />;
      $[29] = description;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <meta property="og:type" content="website" key="ogType" />;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    if ($[32] !== title) {
      t10 = <meta property="og:title" content={title} key="ogTitle" />;
      $[32] = title;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    if ($[34] !== description) {
      t11 = <meta property="og:description" content={description} key="ogDescription" />;
      $[34] = description;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    if ($[36] !== canonicalUrl) {
      t12 = <meta property="og:url" content={canonicalUrl} key="ogUrl" />;
      $[36] = canonicalUrl;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    if ($[38] !== shareImage) {
      t13 = <meta property="og:image" content={shareImage} key="ogImage" />;
      $[38] = shareImage;
      $[39] = t13;
    } else {
      t13 = $[39];
    }
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <meta name="twitter:card" content="summary_large_image" key="twitterCard" />;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    if ($[41] !== title) {
      t15 = <meta name="twitter:text:title" content={title} key="twitterTitle" />;
      $[41] = title;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    if ($[43] !== description) {
      t16 = <meta name="twitter:text:description" content={description} key="twitterDescription" />;
      $[43] = description;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    if ($[45] !== canonicalUrl) {
      t17 = <link rel="canonical" href={canonicalUrl} key="canonicalUrl" />;
      $[45] = canonicalUrl;
      $[46] = t17;
    } else {
      t17 = $[46];
    }
    t4 = "application/ld+json";
    t5 = "stopStructuredData";
    t3 = JSON.stringify(jsonLd);
    $[0] = preferredStopName;
    $[1] = stop;
    $[2] = stopType;
    $[3] = t1;
    $[4] = t2;
    $[5] = T0;
    $[6] = t10;
    $[7] = t11;
    $[8] = t12;
    $[9] = t13;
    $[10] = t14;
    $[11] = t15;
    $[12] = t16;
    $[13] = t17;
    $[14] = t18;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    T0 = $[5];
    t10 = $[6];
    t11 = $[7];
    t12 = $[8];
    t13 = $[9];
    t14 = $[10];
    t15 = $[11];
    t16 = $[12];
    t17 = $[13];
    t18 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t19;
  if ($[47] !== t3) {
    t19 = {
      __html: t3
    };
    $[47] = t3;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t19 || $[50] !== t4 || $[51] !== t5) {
    t20 = <script type={t4} key={t5} dangerouslySetInnerHTML={t19} />;
    $[49] = t19;
    $[50] = t4;
    $[51] = t5;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== T0 || $[54] !== t10 || $[55] !== t11 || $[56] !== t12 || $[57] !== t13 || $[58] !== t14 || $[59] !== t15 || $[60] !== t16 || $[61] !== t17 || $[62] !== t20 || $[63] !== t6 || $[64] !== t7 || $[65] !== t8 || $[66] !== t9) {
    t21 = <T0>{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t20}</T0>;
    $[53] = T0;
    $[54] = t10;
    $[55] = t11;
    $[56] = t12;
    $[57] = t13;
    $[58] = t14;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
    $[62] = t20;
    $[63] = t6;
    $[64] = t7;
    $[65] = t8;
    $[66] = t9;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== children || $[69] !== t18 || $[70] !== t21) {
    t22 = <div className={t18}>{t21}{children}</div>;
    $[68] = children;
    $[69] = t18;
    $[70] = t21;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  return t22;
};
const ViewUpdatesLink = t0 => {
  const $ = _c(5);
  const {
    trip_id,
    children,
    stopId,
    trip_date,
    vehicle_id,
    destinationStopId
  } = t0;
  let t1;
  if ($[0] !== trip_date) {
    t1 = formatNzDateKey(trip_date);
    $[0] = trip_date;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const datePart = t1;
  const t2 = trip_id ? `/trip/${encodeURIComponent(trip_id)}?stop_id=${stopId}&date=${encodeURIComponent(datePart)}${destinationStopId ? `&destination=${encodeURIComponent(destinationStopId)}` : ""}${vehicle_id ? `&vehicle_id=${vehicle_id}` : ""}` : `/trip/trip-unavailable?stop_id=${stopId}`;
  let t3;
  if ($[2] !== children || $[3] !== t2) {
    t3 = <Link href={t2} className="block min-h-[1px] outline-none ring-blue-500/50 transition-colors duration-150 hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10 md:rounded-lg">{children}</Link>;
    $[2] = children;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
const VehicleDetails = t0 => {
  const $ = _c(12);
  const {
    vehicle
  } = t0;
  const hasVehicleDetails = vehicle && (vehicle.registration_number !== undefined || vehicle.vehicle_id !== undefined || vehicle.decks !== undefined || vehicle.is_electric !== undefined);
  if (!hasVehicleDetails) {
    return null;
  }
  let t1;
  if ($[0] !== vehicle) {
    t1 = shouldShowElectricTransportIcon(3, vehicle.vehicle_id, vehicle) ? <BusElectric className="size-[0.875rem] shrink-0" /> : null;
    $[0] = vehicle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== vehicle) {
    t2 = shouldShowDoubleDeckerIcon(vehicle) ? <BusDoubleDecker className="size-[0.875rem] shrink-0" /> : null;
    $[2] = vehicle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== vehicle) {
    t3 = formatVehicleIdentity(vehicle);
    $[4] = vehicle;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="shrink-0 opacity-60">{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <h2 className="relative flex flex-wrap items-center gap-1 pt-1 text-sm leading-none">{t1}{t2}{t4}</h2>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
const DepartureTimeCell = t0 => {
  const $ = _c(17);
  const {
    aimed,
    expected,
    isTripCancelled,
    seconds,
    platformKey
  } = t0;
  let t1;
  if ($[0] !== seconds) {
    t1 = Number.isFinite(seconds) && seconds < -30;
    $[0] = seconds;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isRecentPastDeparture = t1;
  let t2;
  if ($[2] !== aimed || $[3] !== expected || $[4] !== isRecentPastDeparture || $[5] !== isTripCancelled || $[6] !== seconds) {
    t2 = isRecentPastDeparture ? <><span className="">–</span><span className="text-sm leading-none"><br />{formatRelativeMinutesAgo(seconds)}</span></> : expected && Math.round(seconds) < 3600 ? seconds < 70 ? <span className="font-bold">Due</span> : <>{formatRelativeMinutes(seconds)}<span className="text-sm leading-none opacity-60"><br />{new Date(expected).toLocaleTimeString("en-AU", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          timeZone: "Pacific/Auckland"
        })}</span></> : <span className={isTripCancelled ? "pt-1 text-sm leading-none opacity-60" : ""}>{new Date(aimed).toLocaleTimeString("en-AU", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: "Pacific/Auckland"
      })}</span>;
    $[2] = aimed;
    $[3] = expected;
    $[4] = isRecentPastDeparture;
    $[5] = isTripCancelled;
    $[6] = seconds;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== isTripCancelled) {
    t3 = isTripCancelled && <span className="font-bold leading-none text-rose-600 dark:text-rose-500"><br />Cancelled</span>;
    $[8] = isTripCancelled;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== isTripCancelled || $[11] !== platformKey) {
    t4 = platformKey && !isTripCancelled && <span className="block pt-1 text-sm leading-none opacity-60">{`Platform ${platformKey}`}</span>;
    $[10] = isTripCancelled;
    $[11] = platformKey;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = <div className="w-[clamp(2rem,5.5rem,5.5rem)] shrink text-right tabular-nums leading-none">{t2}{t3}{t4}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
const ArrivalTimeCell = t0 => {
  const $ = _c(12);
  const {
    destinationExpectedTime,
    destinationScheduledTime,
    destinationSeconds,
    destination_stop_name,
    isTripCancelled,
    peek
  } = t0;
  if (!destination_stop_name || isTripCancelled || peek) {
    return null;
  }
  const destinationSecondsFromNow = destinationSeconds / 1000;
  let t1;
  if ($[0] !== destinationSecondsFromNow) {
    t1 = Number.isFinite(destinationSecondsFromNow) && destinationSecondsFromNow >= -30 && destinationSecondsFromNow < 70;
    $[0] = destinationSecondsFromNow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isArrivalDue = t1;
  let t2;
  if ($[2] !== destinationSecondsFromNow) {
    t2 = Number.isFinite(destinationSecondsFromNow) && destinationSecondsFromNow < -30;
    $[2] = destinationSecondsFromNow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isRecentPastArrival = t2;
  let t3;
  if ($[4] !== destinationExpectedTime || $[5] !== destinationScheduledTime || $[6] !== destinationSecondsFromNow || $[7] !== isArrivalDue || $[8] !== isRecentPastArrival) {
    t3 = destinationExpectedTime ? <><div>{isArrivalDue ? "Due" : isRecentPastArrival ? formatRelativeMinutesAgo(destinationSecondsFromNow) : formatRelativeMinutes(destinationSecondsFromNow)}</div><span className="text-sm leading-none opacity-60">{new Date(destinationExpectedTime).toLocaleTimeString("en-AU", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
          timeZone: "Pacific/Auckland"
        })}</span></> : destinationScheduledTime && <div>{new Date(destinationScheduledTime).toLocaleTimeString("en-AU", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: "Pacific/Auckland"
      })}</div>;
    $[4] = destinationExpectedTime;
    $[5] = destinationScheduledTime;
    $[6] = destinationSecondsFromNow;
    $[7] = isArrivalDue;
    $[8] = isRecentPastArrival;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t3) {
    t4 = <div className="hidden w-[clamp(2rem,5.5rem,5.5rem)] shrink-0 text-right tabular-nums leading-none 3xs:block">{t3}</div>;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};
const Expected = t0 => {
  const $ = _c(38);
  const {
    aimed,
    bikes_allowed,
    bus_replaced,
    trip_headsign,
    stop_headsign,
    expected,
    route_color,
    route_text_color,
    route_type,
    school,
    routeLabel,
    stopId,
    cancelled,
    stopType,
    seconds,
    trip_id,
    vehicle_id,
    vehicle,
    destination_stop_scheduled_arrival_time,
    destination_stop_expected_arrival_time,
    destination_stop_name,
    destination_stop_id,
    platform,
    peek,
    isTimetable,
    nowMs,
    showRecentDepartures
  } = t0;
  const normalizedRouteColor = route_color?.startsWith("#") ? route_color : `#${route_color}`;
  const isTripCancelled = cancelled && !expected;
  const destinationExpectedTime = destination_stop_expected_arrival_time;
  const destinationScheduledTime = destination_stop_scheduled_arrival_time;
  const destinationSeconds = destinationExpectedTime ? Date.parse(destinationExpectedTime) - nowMs : null;
  let t1;
  if ($[0] !== isTimetable || $[1] !== seconds) {
    t1 = !isTimetable && Number.isFinite(seconds) && seconds < -30 && seconds >= -RECENT_DEPARTURE_WINDOW_MS / 1000;
    $[0] = isTimetable;
    $[1] = seconds;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isRecentPastDeparture = t1;
  let t2;
  if ($[3] !== aimed || $[4] !== expected || $[5] !== nowMs || $[6] !== platform || $[7] !== stopId) {
    t2 = getDisplayPlatformKey(stopId, platform, aimed, expected, nowMs);
    $[3] = aimed;
    $[4] = expected;
    $[5] = nowMs;
    $[6] = platform;
    $[7] = stopId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const displayPlatformKey = t2;
  let t3;
  if ($[9] !== aimed || $[10] !== bikes_allowed || $[11] !== bus_replaced || $[12] !== destinationExpectedTime || $[13] !== destinationScheduledTime || $[14] !== destinationSeconds || $[15] !== destination_stop_id || $[16] !== destination_stop_name || $[17] !== displayPlatformKey || $[18] !== expected || $[19] !== isRecentPastDeparture || $[20] !== isTimetable || $[21] !== isTripCancelled || $[22] !== normalizedRouteColor || $[23] !== peek || $[24] !== routeLabel || $[25] !== route_text_color || $[26] !== route_type || $[27] !== school || $[28] !== seconds || $[29] !== showRecentDepartures || $[30] !== stopId || $[31] !== stopType || $[32] !== stop_headsign || $[33] !== trip_headsign || $[34] !== trip_id || $[35] !== vehicle || $[36] !== vehicle_id) {
    t3 = (isTimetable || isNaN(seconds) || seconds > -30 || showRecentDepartures && seconds >= -RECENT_DEPARTURE_WINDOW_MS / 1000) && <ViewUpdatesLink trip_date={aimed} trip_id={trip_id} stopType={stopType} stopId={stopId} vehicle_id={vehicle_id} destinationStopId={peek ? null : destination_stop_id}><div className={`flex min-h-[1px] items-center gap-x-1 px-[20px] py-3 text-lg ${isRecentPastDeparture ? "opacity-60" : ""}`}><RouteBadge route_color={normalizedRouteColor} route_text_color={route_text_color} route_type={route_type} routeLabel={routeLabel} className="mr-2 h-9 w-9 shrink-0" /><div className="flex-1 leading-none"><h3 className="break-words">{stop_headsign ? stop_headsign : trip_headsign}</h3>{trip_headsign === "School Bus" && <h2 className="pt-1 text-sm leading-none opacity-60">{school}</h2>}{bus_replaced && <h2 className="pt-1 text-sm leading-none opacity-60">Bus replaced</h2>}{bikes_allowed === "not_allowed" && <h2 className="pt-1 text-sm leading-none opacity-60">No bikes</h2>}<VehicleDetails vehicle={vehicle} /></div><DepartureTimeCell aimed={aimed} expected={expected} isTripCancelled={isTripCancelled} seconds={seconds} platformKey={displayPlatformKey} /><ArrivalTimeCell destinationExpectedTime={destinationExpectedTime} destinationScheduledTime={destinationScheduledTime} destinationSeconds={destinationSeconds} destination_stop_name={destination_stop_name} isTripCancelled={isTripCancelled} peek={peek} /></div></ViewUpdatesLink>;
    $[9] = aimed;
    $[10] = bikes_allowed;
    $[11] = bus_replaced;
    $[12] = destinationExpectedTime;
    $[13] = destinationScheduledTime;
    $[14] = destinationSeconds;
    $[15] = destination_stop_id;
    $[16] = destination_stop_name;
    $[17] = displayPlatformKey;
    $[18] = expected;
    $[19] = isRecentPastDeparture;
    $[20] = isTimetable;
    $[21] = isTripCancelled;
    $[22] = normalizedRouteColor;
    $[23] = peek;
    $[24] = routeLabel;
    $[25] = route_text_color;
    $[26] = route_type;
    $[27] = school;
    $[28] = seconds;
    $[29] = showRecentDepartures;
    $[30] = stopId;
    $[31] = stopType;
    $[32] = stop_headsign;
    $[33] = trip_headsign;
    $[34] = trip_id;
    $[35] = vehicle;
    $[36] = vehicle_id;
    $[37] = t3;
  } else {
    t3 = $[37];
  }
  return t3;
};
export default function StopPage({
  alertsAtStop,
  isApple,
  isClient,
  dismissedAlerts,
  feedInfo,
  isDark,
  mapsSupported,
  nearbyStops,
  preferredServices,
  preferredStopNames,
  servicesAtStop,
  setDismissedAlerts,
  setPreferredServices,
  setPreferredStopNames,
  stopId,
  stop,
  stopType
}) {
  const router = useRouter();
  const [viewMode, setViewMode] = useState("live"); // "live" | "timetable"
  const isTimetable = viewMode === "timetable";
  const isArrivalOnly = Boolean(stop?.is_arrival_only);
  const [viewModeModalIsOpen, setViewModeModalIsOpen] = useState(false);
  const openViewModeModal = useCallback(() => {
    setViewModeModalIsOpen(true);
  }, []);
  const closeViewModeModal = useCallback(() => {
    setViewModeModalIsOpen(false);
  }, []);
  const clampToFeedRange = useCallback(iso => {
    if (!feedInfo?.start_date || !feedInfo?.end_date) return iso;
    if (iso < feedInfo.start_date) return feedInfo.start_date;
    if (iso > feedInfo.end_date) return feedInfo.end_date;
    return iso;
  }, [feedInfo]);
  const getTodayNZ = () => new Date().toLocaleString("en-CA", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  });
  const setViewModeAndClose = useCallback(next => {
    setViewMode(current => current === next ? current : next);
    if (next === "timetable") {
      const today = getTodayNZ();
      setTtDate(clampToFeedRange(today));
    }
    setViewModeModalIsOpen(false);
  }, [setViewMode, clampToFeedRange]);
  const todayNZ = useMemo(() => new Date().toLocaleString("en-CA", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }), []);
  const [ttDate, setTtDate] = useState(() => todayNZ);

  // When switching into timetable mode, ensure the date is inside the feed window
  useEffect(() => {
    if (viewMode !== "timetable") return;
    setTtDate(d => clampToFeedRange(d));
  }, [viewMode, clampToFeedRange]);
  const addDays = useCallback((iso_0, delta) => {
    const d_0 = new Date(`${iso_0}T00:00:00`);
    d_0.setDate(d_0.getDate() + delta);
    return d_0.toLocaleString("en-CA", {
      timeZone: "Pacific/Auckland",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    });
  }, []);
  const [time, setTime] = useState(new Date());
  const [dateOffset, setDateOffset] = useState(0);
  const [direction, setDirection] = useState(null);
  const [favourites, setFavourites] = useLocalStorage("favourites", []);
  const [recents, setRecents] = useLocalStorage("recents", []);
  const [destinations, setDestinations] = useLocalStorage("destinations", []);
  const [destinationModalIsOpen, setDestinationModalIsOpen] = useState(false);
  useEffect(() => {
    // Timetable mode can be huge; avoid forcing a full list rerender every 5s.
    if (isTimetable) {
      setTime(new Date(Date.now() + dateOffset));
      return;
    }
    const interval = setInterval(() => {
      setTime(new Date(Date.now() + dateOffset));
    }, 5000);
    return () => {
      clearInterval(interval);
    };
  }, [dateOffset, isTimetable]);
  useSWR(`/api/local-time`, {
    fetcher: url => fetch(url, {
      cache: "no-store"
    }).then(res => res.json()),
    refreshInterval: 0,
    revalidateOnFocus: false,
    onSuccess: d_1 => {
      const serverNow = new Date(d_1.date).getTime();
      const localNow = Date.now();
      const offset = serverNow - localNow;
      setDateOffset(Math.abs(offset) > 10000 ? offset : 0);
    }
  });
  useTrackRecentStop({
    isFallback: router.isFallback,
    setRecents,
    stopId
  });
  const selectedDestination = useMemo(() => destinations?.find(stop_0 => getStoredStopId(stop_0) === stopId)?.destination ?? null, [destinations, stopId]);
  const clearDestination = () => {
    const updatedDestinations = destinations?.filter(el => getStoredStopId(el) !== stopId);
    setDestinations(updatedDestinations);
  };
  const getStopDisplayName = stop_1 => {
    const preferredName = preferredStopNames.find(pref => getStoredStopId(pref) === stop_1.stop_id)?.name;
    return preferredName || stop_1.stop_name;
  };
  const [preferredDirections, setPreferredDirections] = useLocalStorage("preferred_directions", []);
  const [isDirectionResolved, setIsDirectionResolved] = useState(false);
  const stationDirectionLabels = getDirectionControlLabelsFromStop(stop);
  const supportsDirectionPicker = !isArrivalOnly && shouldShowDirectionPicker(stop, stopType);
  useEffect(() => {
    if (!stopId) return;
    const noDirectionPicker = !supportsDirectionPicker;

    // For stops with no direction picker, keep direction null so we don't
    // bounce between this effect and the clearing effect.
    const foundDirection = noDirectionPicker ? null : preferredDirections?.find(stop_2 => getStoredStopId(stop_2) === stopId)?.direction ?? "inbound";
    setDirection(prev => prev === foundDirection ? prev : foundDirection);
    setIsDirectionResolved(true);
  }, [preferredDirections, stopId, supportsDirectionPicker]);

  // Clear saved direction for stops that should not expose a direction picker
  useEffect(() => {
    if (!stopId) return;
    const noDirectionPicker_0 = !supportsDirectionPicker;
    if (noDirectionPicker_0) {
      if (direction !== null) setDirection(null);
      setPreferredDirections((prev_0 = []) => {
        const next_0 = prev_0.filter(p => getStoredStopId(p) !== stopId);
        return next_0.length === prev_0.length ? prev_0 : next_0;
      });
      setIsDirectionResolved(true);
    }
  }, [stopId, supportsDirectionPicker, direction, setPreferredDirections]);
  const persistedPreferredServices = useMemo(() => preferredServices.find(stop_3 => getStoredStopId(stop_3) === stopId)?.services || [], [preferredServices, stopId]);
  const preferredStopName = useMemo(() => preferredStopNames.find(stop_4 => getStoredStopId(stop_4) === stopId)?.name || null, [preferredStopNames, stopId]);
  const [preferredStopNameTemp, setPreferredStopNameTemp] = useState(preferredStopName ? preferredStopName : stop?.stop_name);
  const [preferredServicesTemp, setPreferredServicesTemp] = useState([]);
  useEffect(() => {
    setPreferredServicesTemp(persistedPreferredServices);
  }, [persistedPreferredServices]);
  const togglePreferredService = useCallback(event => {
    let updatedList = [...preferredServicesTemp];
    const routeId = event.target.value;
    if (event.target.checked) {
      // Add a new object with route_id
      updatedList = [...updatedList, {
        route_id: routeId
      }];
    } else {
      // Remove the object with the matching route_id
      updatedList = updatedList.filter(service => service.route_id !== routeId);
    }
    setPreferredServicesTemp(updatedList);
  }, [preferredServicesTemp]);
  const savePreferredStopName = value => {
    const updatedPreferredStopNames = preferredStopNames.some(el_1 => getStoredStopId(el_1) === stopId) ? preferredStopNames.map(el_0 => getStoredStopId(el_0) === stopId ? withStoredStopId({
      ...el_0,
      name: value
    }, value && stopId) : el_0) : [...preferredStopNames, withStoredStopId({
      name: value
    }, stopId)];
    setPreferredStopNames(updatedPreferredStopNames.filter(el_2 => el_2.name?.length));
    setRenameModalIsOpen(false);
  };
  const savePreferredServices = value_0 => {
    const toSet = servicesAtStop.length > value_0.length ? value_0 : [];
    const updatedPreferredServices = preferredServices.some(el_4 => getStoredStopId(el_4) === stopId) ? preferredServices.map(el_3 => getStoredStopId(el_3) === stopId ? toSet.length ? withStoredStopId({
      ...el_3,
      services: toSet
    }, stopId) : null : el_3) : [...preferredServices, withStoredStopId({
      services: toSet
    }, stopId)];
    setPreferredServices(updatedPreferredServices.filter(Boolean));
    setServicesModalIsOpen(false);
    setPeek(false);
  };
  const virtuosoRef = useRef(null);
  const bigTitle = useRef(null);
  const inputRef = useRef();
  const [titleVisible, setTitleVisible] = useState(false);
  useEffect(() => {
    var intersectionObserver = new IntersectionObserver(function (entries) {
      setTitleVisible(!entries[0].isIntersecting);
    }, {
      rootMargin: "-65px 0px 0px 0px",
      threshold: 0.1
    });
    bigTitle?.current && intersectionObserver.observe(bigTitle.current);
    return () => {
      intersectionObserver.disconnect();
    };
  }, []);
  const [renameModalIsOpen, setRenameModalIsOpen] = useState(false);
  const [servicesModalIsOpen, setServicesModalIsOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState();
  const preferredServicesAtStop = persistedPreferredServices?.map(service_0 => service_0.route_id).join(",");

  // Persist “Show all / Show filtered” per‑stop using a single LS item
  const [peekSettings, setPeekSettings] = useLocalStorage("peek", []);
  const peek = useMemo(() => (peekSettings || []).find(p_0 => getStoredStopId(p_0) === stopId)?.value ?? false, [peekSettings, stopId]);
  const setPeek = useCallback(value_1 => setPeekSettings((prev_1 = []) => {
    const index = prev_1.findIndex(p_1 => getStoredStopId(p_1) === stopId);
    if (index !== -1) {
      const updated = [...prev_1];
      updated[index] = withStoredStopId({
        value: value_1
      }, stopId);
      return updated;
    }
    return [...prev_1, withStoredStopId({
      value: value_1
    }, stopId)];
  }), [setPeekSettings, stopId]);

  // Global "Show bus details" preference (applies to all bus stops)
  const [busDetails, setBusDetails] = useLocalStorage("bus_details", false);
  const [showRecentDepartures, setShowRecentDepartures] = useLocalStorage("show_recent_departures", false);

  // Ensure localStorage-backed preferences (some come from parent props) have
  // had at least one client render to hydrate before building the SWR key.
  const [prefsReady, setPrefsReady] = useState(false);
  useEffect(() => {
    if (!isClient) return;
    const raf = requestAnimationFrame(() => setPrefsReady(true));
    return () => cancelAnimationFrame(raf);
  }, [isClient]);
  const hydratedSelectedDestination = isClient && prefsReady ? selectedDestination : null;
  const hydratedPeek = isClient && prefsReady ? peek : false;
  const departuresKey = useMemo(() => {
    // Avoid an initial unfiltered/incorrect fetch on hard refresh while
    // localStorage-backed prefs hydrate.
    if (!stopId || !prefsReady || isArrivalOnly) return null;

    // Timetable mode: keyed by (stopId, date, destination, services, direction)
    if (isTimetable) {
      const date = clampToFeedRange(ttDate);
      const timetableParams = new URLSearchParams({
        stop_id: stopId,
        date,
        ...(direction && {
          direction
        }),
        ...(preferredServicesAtStop && !peek && {
          services: preferredServicesAtStop
        }),
        ...(selectedDestination && !peek && {
          destination: selectedDestination.stop_id
        })
      });
      return `/api/stopdeparturesbydate?${timetableParams.toString()}`;
    }

    // Live mode
    if (!isDirectionResolved) return null;
    const departuresQueryParams = new URLSearchParams({
      ...(direction && {
        direction
      }),
      ...(preferredServicesAtStop && !peek && {
        services: preferredServicesAtStop
      }),
      ...(selectedDestination && !peek && {
        destination: selectedDestination.stop_id
      }),
      ...(showRecentDepartures ? {
        recent: "true"
      } : {}),
      ...(stopType === "bus" && busDetails ? {
        vehicles: "true"
      } : {})
    });
    return `/api/stopdepartures/${stopId}?${departuresQueryParams.toString()}`;
  }, [stopId, prefsReady, isTimetable, isArrivalOnly, ttDate, clampToFeedRange, isDirectionResolved, direction, preferredServicesAtStop, peek, selectedDestination, showRecentDepartures, stopType, busDetails]);
  const hasActiveDestinationFilter = Boolean(hydratedSelectedDestination && !hydratedPeek);
  const hasActiveServicesFilter = Boolean(preferredServicesAtStop && !hydratedPeek);
  const hasActiveDirectionFilter = Boolean(direction);
  const departureEmptyState = getStopDepartureEmptyState({
    isArrivalOnly,
    isTimetable,
    hasDirectionFilter: hasActiveDirectionFilter,
    hasDestinationFilter: hasActiveDestinationFilter,
    hasServicesFilter: hasActiveServicesFilter
  });
  const {
    data: rawDepartures,
    isLoading,
    isValidating,
    error
  } = useSWR(departuresKey, fetcher, {
    // Cache reuse is scoped to the complete stop/filter key.
    keepPreviousData: false,
    // Timetable should not revalidate/poll
    refreshInterval: isTimetable ? 0 : 20000,
    revalidateOnFocus: !isTimetable,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    dedupingInterval: isTimetable ? 600000 : 10000,
    onSuccess: () => {
      // Only meaningful for live-mode stale UI
      if (!isTimetable) setLastUpdated(Date.now());
    }
  });

  // The existing clock keeps countdowns current. One board-wide deadline also
  // removes expired predictions between those five-second ticks.
  useEffect(() => {
    if (isTimetable || !departuresKey) return;
    const nowMs = Date.now() + dateOffset;
    const expiry = nextDeparturePredictionExpiry(rawDepartures, time.getTime());
    if (expiry === null) return;
    const timer = setTimeout(() => setTime(new Date(Date.now() + dateOffset)), Math.max(0, Math.min(expiry - nowMs, 2_147_483_647)));
    return () => clearTimeout(timer);
  }, [rawDepartures, departuresKey, isTimetable, dateOffset, time]);
  const departures = useMemo(() => {
    if (!departuresKey) return undefined;
    return isTimetable ? rawDepartures : applyDepartureBoardFreshness(rawDepartures, Math.max(time.getTime(), Date.now() + dateOffset));
  }, [rawDepartures, departuresKey, isTimetable, time, dateOffset]);
  const timetableData = useMemo(() => {
    // Always return an array. The API can return `{ message: ... }` when filtered to obscurity.
    const baseList = Array.isArray(departures) ? departures : [];

    // When not in timetable mode, we still want callers to get a sane array.
    if (!isTimetable) return baseList;
    const today_0 = getTodayNZ();
    const isToday = clampToFeedRange(ttDate) === today_0;
    if (!isToday || baseList.length === 0) return baseList;

    // Use the same server-time offset you already compute from /api/local-time
    const nowMs_0 = Date.now() + dateOffset;

    // Pick the best timetable time field to compare (scheduled is usually right for timetable)
    const getMs = d_2 => Date.parse(d_2.expected_departure_time || d_2.scheduled_departure_time);
    let inserted = false;
    const out = [];
    for (let i = 0; i < baseList.length; i++) {
      const dep = baseList[i];
      const depMs = getMs(dep);
      if (!inserted && Number.isFinite(depMs) && depMs >= nowMs_0) {
        out.push({
          __type: "now-marker",
          id: "now"
        });
        inserted = true;
      }
      out.push(dep);
    }

    // If everything is already in the past, put it at the end (optional)
    if (!inserted) out.push({
      __type: "now-marker",
      id: "now"
    });
    return out;
  }, [departures, isTimetable, ttDate, dateOffset, clampToFeedRange]);
  const nowMarkerIndex = useMemo(() => {
    if (!isTimetable) return null;
    if (!Array.isArray(timetableData)) return null;
    const idx = timetableData.findIndex(x => x?.__type === "now-marker");
    return idx >= 0 ? idx : null;
  }, [isTimetable, timetableData]);
  const jumpToNow = useCallback(() => {
    if (nowMarkerIndex === null) return;

    // scrollToIndex supports an offset; negative pulls the target below the sticky header
    virtuosoRef.current?.scrollToIndex({
      index: nowMarkerIndex,
      align: "start",
      behavior: "smooth",
      offset: -120
    });
  }, [nowMarkerIndex]);
  const isTimetableToday = useMemo(() => {
    if (!isTimetable) return false;
    return clampToFeedRange(ttDate) === getTodayNZ();
  }, [isTimetable, ttDate, clampToFeedRange]);
  const groupedDepartures = useMemo(() => {
    if (!departures || departures?.message) {
      return null;
    }

    // Timetable API is already scoped to a single day, so skip the per-row
    // grouping work and just wrap it in the shape the renderer expects.
    if (isTimetable) {
      const date_0 = clampToFeedRange(ttDate);
      return [[date_0, departures]];
    }
    return Object.entries(groupByDepartureDate(departures, ["estimated_departure_time", "scheduled_departure_time"]));
  }, [departures, isTimetable, ttDate, clampToFeedRange]);
  const liveHeaderDate = useMemo(() => {
    if (groupedDepartures?.length) {
      return groupedDepartures[0][0];
    }
    return getTodayNZ();
  }, [groupedDepartures]);
  const {
    data: service_alerts
  } = useSWR(stopId && `/api/servicealerts/stop-id/${stopId}`, {
    fetcher: fetcher,
    refreshInterval: 600000,
    // 10 minutes
    revalidateOnFocus: true,
    dedupingInterval: 600000,
    fallbackData: Array.isArray(alertsAtStop) ? alertsAtStop : []
  });
  const filteredAlerts = useMemo(() => {
    const serviceAlertsAtStop = Array.isArray(service_alerts) ? service_alerts : Array.isArray(alertsAtStop) ? alertsAtStop : [];
    const preferredServicesForStop = preferredServices.find(item => getStoredStopId(item) === stopId)?.services || [];
    return serviceAlertsAtStop.filter(alert =>
    // Check if the alert has not been dismissed
    !(isClient && prefsReady ? dismissedAlerts : [])?.includes(alert.alert_id) &&
    // Check if the stop is not a train stop or the alert is not a bus replacement
    !(stopType === "train" && alert.header_text?.toLowerCase().includes("bus")) && (
    // Check if preferredServicesForStop is empty or if the alert is related to a preferred service
    preferredServicesForStop.length === 0 || hydratedPeek || alert.routeIds?.some(routeId_0 => preferredServicesForStop.some(service_1 => service_1.route_id === routeId_0))) && (!hydratedSelectedDestination || hydratedPeek || alert.stop_ids?.includes(hydratedSelectedDestination.stop_id)) && (
    // Existing condition to filter by departure stop
    alert.stop_ids.includes(stopId) || !alert.stop_ids));
  }, [alertsAtStop, service_alerts, preferredServices, stopId, isClient, prefsReady, dismissedAlerts, stopType, hydratedPeek, hydratedSelectedDestination]);
  if (error && !departures) {
    return <PageWrapper nearbyStops={nearbyStops} servicesAtStop={servicesAtStop} stop={stop} stopType={stopType} preferredStopName={preferredStopName}>
        <div className="sticky top-0 z-20 mb-2 flex flex-row justify-between bg-white px-[20px] pb-2 pt-4 dark:bg-black">
          <BackLink stopType={stopType} />
          <div className="flex space-x-1.5">
            {isLoading && <div className="mr-1 grid h-9 w-9 place-items-center">
                <Spinner className="h-[1.375rem] w-[1.375rem] animate-spin" style={getStopSpinnerStyle(stopType)} />
              </div>}
          </div>
        </div>
        <div className="flex items-start justify-between">
          <h1 className="w-full overflow-ellipsis text-pretty px-[20px] text-3xl font-semibold">
            {preferredStopName && isClient ? preferredStopName : stop.stop_name}
          </h1>
        </div>
        <div className="p-5">Unable to get realtime updates.</div>
        <button onClick={() => router.reload()} className="mx-[20px] rounded-full bg-zinc-400/10 px-4 py-1 text-lg font-semibold text-blue-500 ring-blue-500/50 transition-colors hover:bg-zinc-400/20 focus:outline-none focus-visible:ring active:bg-zinc-400/20 dark:text-blue-400">
          Retry
        </button>
      </PageWrapper>;
  }
  if (router.isFallback) {
    const fallbackStopType = checkStopType(router.query.stop_id);
    return <div className="ml-[20px] mt-[16px] grid h-9 w-9 place-items-center">
        <Spinner className="h-6 w-6 animate-spin opacity-60" style={getStopSpinnerStyle(fallbackStopType)} />
      </div>;
  }
  return <PageWrapper nearbyStops={nearbyStops} servicesAtStop={servicesAtStop} stop={stop} stopType={stopType} preferredStopName={preferredStopName}>
      <div className="sticky top-0 z-20 mb-2 flex flex-row justify-between bg-white px-[20px] pb-2 pt-4 dark:bg-black">
        <BackLink stopType={stopType} />
        <div className={`flex-1 select-none overflow-hidden overflow-ellipsis whitespace-nowrap px-2 py-1 text-xl font-semibold transition-opacity ${titleVisible ? "opacity-100" : "opacity-0"}`} onClick={() => window.scrollTo({
        top: 0,
        behavior: "smooth"
      })} aria-hidden="true">
          {preferredStopName && isClient ? preferredStopName : stop.stop_name}
        </div>
        <div className="flex space-x-1.5">
          {isValidating && <div className="mr-1 grid h-9 w-9 place-items-center">
              <Spinner className="h-[1.375rem] w-[1.375rem] animate-spin" style={getStopSpinnerStyle(stopType)} />
            </div>}

          <FavouriteButton stopId={stopId} favourites={favourites} setFavourites={setFavourites} />

          <OptionsMenu isApple={isApple}>
            <div className="flex flex-col text-lg">
              <MenuItem as="button" onClick={() => {
              setRenameModalIsOpen(true);
              setPreferredStopNameTemp(preferredStopName || stop.stop_name);
            }} className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3">
                Rename Stop…
              </MenuItem>

              {servicesAtStop.length > 1 && <MenuItem as="button" className={`flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3`} onClick={() => {
              setServicesModalIsOpen(true);
            }}>
                  <span>Filter by Service…</span>
                  <span className="shrink-0 text-zinc-400">
                    {preferredServicesTemp?.length ? preferredServicesTemp?.length : servicesAtStop.length}{" "}
                    of {servicesAtStop.length}
                  </span>
                </MenuItem>}

              {(stopType === "bus" || stopType === "train") && <>
                  <MenuItem as="button" className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={() => {
                setDestinationModalIsOpen(true);
              }}>
                    Filter by Destination Stop…
                  </MenuItem>
                  <MenuItem as="button" className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={() => setShowRecentDepartures(!showRecentDepartures)}>
                    <span>Show Recent Departures</span>
                    <span className="shrink-0 text-zinc-400">
                      {showRecentDepartures ? "On" : "Off"}
                    </span>
                  </MenuItem>
                  {stopType === "bus" && <MenuItem as="button" className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={() => setBusDetails(!busDetails)}>
                      <span>Show Bus Details</span>
                      <span className="shrink-0 text-zinc-400">
                        {busDetails ? "On" : "Off"}
                      </span>
                    </MenuItem>}
                </>}

              {(preferredServicesAtStop?.length > 0 || selectedDestination) && <MenuItem as="button" className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3" onClick={() => {
              setPreferredServicesTemp([]);
              savePreferredServices([]);
              clearDestination();
            }}>
                  Clear Filters
                </MenuItem>}
              {mapsSupported && <MenuItem as={Link} href={`/map?stop=${stop.stop_id}&lat=${stop.stop_lat}&lng=${stop.stop_lon}`} className="flex w-full justify-between px-5 py-3 text-left hover:bg-zinc-400/10 active:bg-zinc-400/10 data-[focus]:bg-zinc-400/10 sm:p-3">
                  View on Map
                </MenuItem>}
            </div>
          </OptionsMenu>

          {/* View options modal */}
          <Modal isOpen={viewModeModalIsOpen} onClose={closeViewModeModal} title="Switch View">
            <div className="flex flex-col">
              {/* Departures */}
              <label>
                <div className="flex cursor-pointer items-start gap-x-4 px-[20px] py-2 outline-none hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10" onClick={() => setViewModeAndClose("live")}>
                  <input type="checkbox" readOnly checked={!isTimetable} className="h-5 w-5 appearance-none rounded-lg bg-zinc-400/20 outline-none ring-1 ring-inset ring-white/20 transition-all duration-150 checked:bg-blue-500 focus:outline-none focus-visible:ring" />

                  <div className="flex flex-col leading-tight">
                    <div className="font-semibold">Departures</div>
                    <div className="text-base opacity-60">
                      Upcoming departures and live updates
                    </div>
                  </div>
                </div>
              </label>

              <label>
                <div className="flex cursor-pointer items-start gap-x-4 px-[20px] py-3 outline-none hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10" onClick={() => setViewModeAndClose("timetable")}>
                  <input type="checkbox" readOnly checked={isTimetable} className="h-5 w-5 appearance-none rounded-lg bg-zinc-400/20 outline-none ring-1 ring-inset ring-white/20 transition-all duration-150 checked:bg-blue-500 focus:outline-none focus-visible:ring" />

                  <div className="flex flex-col leading-tight">
                    <div className="font-semibold">Timetable</div>
                    <div className="text-base opacity-60">
                      Scheduled times for a selected day
                    </div>
                  </div>
                </div>
              </label>

              {/* Cancel row */}
              <div className="flex flex-row-reverse gap-2 p-3 font-semibold text-blue-500 dark:text-white">
                <DialogAction onClick={closeViewModeModal} type="button">
                  Cancel
                </DialogAction>
              </div>
            </div>
          </Modal>

          <Modal isOpen={servicesModalIsOpen} onClose={() => setServicesModalIsOpen(false)} title="Filter by Service">
            <form onSubmit={e => {
            e.preventDefault();
            savePreferredServices(preferredServicesTemp);
          }}>
              <div className="relative flex max-h-[calc(var(--vvh)-13rem)] flex-col overflow-y-auto">
                {servicesAtStop?.map((service_2, k) => {
                const schoolBusSchool = school_bus_schools.get(service_2.route_short_name);
                return <label key={k}>
                      <div className="flex cursor-pointer items-center gap-x-4 px-[20px] py-3 outline-none hover:bg-zinc-400/10 focus-visible:ring focus-visible:ring-inset active:bg-zinc-400/10">
                        <input type="checkbox" value={service_2.route_id} onChange={togglePreferredService} defaultChecked={preferredServicesTemp?.some(f => f.route_id === service_2.route_id)} className="h-5 w-5 appearance-none rounded-lg bg-zinc-400/20 outline-none ring-1 ring-inset ring-white/20 transition-all duration-150 checked:bg-blue-500 focus:outline-none focus-visible:ring" />
                        <RouteBadge route_color={service_2.route_color} route_text_color={service_2.route_text_color} route_type={service_2.route_type} routeLabel={service_2.route_short_name} className="h-9 w-9" />
                        <div className="flex-1 leading-none">
                          <div>{getServiceDisplayName(service_2)}</div>
                          {schoolBusSchool ? <div className="pt-1 text-sm leading-none opacity-60">
                              {schoolBusSchool}
                            </div> : null}
                        </div>
                      </div>
                    </label>;
              })}
              </div>

              <div className="flex flex-row-reverse gap-2 p-3 font-semibold text-blue-500 dark:text-white">
                <DialogAction type="submit">Save</DialogAction>
                <DialogAction onClick={() => {
                setPreferredServicesTemp(preferredServices.find(stop_6 => getStoredStopId(stop_6) === stopId)?.services.length && preferredServices.find(stop_5 => getStoredStopId(stop_5) === stopId)?.services || []);
                setServicesModalIsOpen(false);
              }} type="button">
                  Cancel
                </DialogAction>
                <div className="flex-1" />

                <DialogAction onClick={() => {
                setPreferredServicesTemp([]);
                savePreferredServices([]);
              }} destructive className={preferredServicesTemp?.length > 0 ? "" : "invisible"} type="button" disabled={preferredServicesTemp?.length === 0}>
                  Clear All
                </DialogAction>
              </div>
            </form>
          </Modal>

          <Modal isOpen={renameModalIsOpen} afterEnter={() => {
          window.setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 150);
        }} onClose={() => setRenameModalIsOpen(false)} title={`Rename ${stopType === "bus" ? "Stop" : "Station"}`}>
            <form onSubmit={e_0 => {
            e_0.preventDefault();
            savePreferredStopName(preferredStopNameTemp.trim());
          }}>
              <div className="relative mt-3 px-[20px]">
                <div className="flex rounded ring-1 ring-zinc-500/50 duration-100 focus-within:ring focus-within:ring-blue-500/50">
                  <input type="text" className={`h-12 w-full appearance-none border-none bg-transparent py-2 pl-3 text-lg placeholder-zinc-400 outline-none transition-colors ${preferredStopNameTemp?.length > 0 ? "pr-12" : "pr-3"}`} placeholder={stop.stop_name} value={preferredStopNameTemp ? preferredStopNameTemp : ""} onChange={e_1 => setPreferredStopNameTemp(e_1.target.value)} ref={inputRef} spellCheck="false" autoCapitalize="true" autoComplete="false" autoCorrect="false" enterKeyHint="done" />
                  {preferredStopNameTemp?.length > 0 && <button onClick={() => {
                  inputRef.current.focus();
                  setPreferredStopNameTemp("");
                }} className="pointer-events-auto absolute right-[calc(20px+0.5rem)] top-1.5 z-10 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none ring-blue-500/50 transition-all duration-150 hover:bg-zinc-400/10 focus-visible:ring active:bg-zinc-400/10" type="button">
                      <CloseCross role="display" title="Close" className="size-[1.125rem] text-zinc-500 dark:text-zinc-300" />
                    </button>}
                </div>
              </div>

              <div className="flex flex-row-reverse gap-2 p-3 font-semibold text-blue-500 dark:text-white">
                <DialogAction type="submit">Save</DialogAction>
                <DialogAction onClick={() => setRenameModalIsOpen(false)} type="button">
                  Cancel
                </DialogAction>
              </div>
            </form>
          </Modal>

          <DestinationStopSelector selectedDestination={selectedDestination} destinationModalIsOpen={destinationModalIsOpen} setDestinationModalIsOpen={setDestinationModalIsOpen} destinations={destinations} setDestinations={setDestinations} clearDestination={clearDestination} stopId={stopId} preferredStopNames={preferredStopNames} favourites={favourites} stopType={stopType} setPeek={setPeek} />
        </div>
      </div>
      <div className="flex items-start justify-between">
        <h1 className="w-full overflow-ellipsis text-pretty px-[20px] text-3xl font-semibold" ref={bigTitle}>
          {preferredStopName && isClient ? preferredStopName : stop.stop_name}
        </h1>

        <div className={isTimetable ? "opacity-0" : "op"}>
          <WeatherWidget lat={stop.stop_lat} long={stop.stop_lon} isDark={isDark} />
        </div>
      </div>

      {hydratedSelectedDestination && !hydratedPeek && <h2 className="mx-[20px] flex cursor-pointer items-center gap-x-3 pt-4 text-lg" onClick={() => setDestinationModalIsOpen(true)}>
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-yellow-400/20">
            <DestinationFlag className="ml-1 size-5 shrink-0" />
          </div>
          <div className="leading-tight">
            To:{" "}
            {getStopDisplayName(hydratedSelectedDestination) || hydratedSelectedDestination.stop_name}
          </div>
        </h2>}

      {filteredAlerts?.length > 0 && <div className="flex flex-col gap-y-3 pb-2 pt-[20px] sm:gap-y-4">
          {filteredAlerts?.map((alert_0, key) => <Alert alert_id={alert_0.alert_id} header_text={alert_0.header_text} description_text={alert_0.description_text} url={alert_0.url} key={key} effect={alert_0.effect} severity_level={alert_0.severityLevel} dismissable={true} dismissedAlerts={dismissedAlerts} setDismissedAlerts={setDismissedAlerts} />)}
        </div>}
      {supportsDirectionPicker && (departures || isLoading) && <DirectionControl stopType={stopType} direction={direction} setDirection={setDirection} preferredDirections={preferredDirections} setPreferredDirections={setPreferredDirections} stopId={stopId} directionLabels={stationDirectionLabels} />}
      {isClient && !isTimetable && !isLoading && departures?.message && <div className="sticky top-12 z-10 bg-white dark:bg-black">
          <div className="flex items-end justify-between gap-x-4 px-[20px] py-10 pb-1.5 pt-3 capitalize leading-tight">
            <button type="button" onClick={openViewModeModal} className="group min-w-0 flex-1 select-none text-left outline-none" title="Switch view">
              <div className="flex items-center gap-0.5 text-sm font-semibold opacity-60">
                Departures
                <ChevronDownSmall className="size-4" />
              </div>
              <div className="flex min-w-0 items-center gap-2">
                <div className="flex w-full min-w-0 flex-wrap @container">
                  {isClient && <span className="mr-1 inline-block truncate font-bold capitalize">
                      {getRelativeDate(new Date(`${liveHeaderDate}T00:00:00`))}
                    </span>}
                  <span className="hidden truncate opacity-60 @[12rem]:inline">
                    {new Date(`${liveHeaderDate}T00:00:00`).toLocaleString("en-AU", {
                  weekday: "long",
                  day: "numeric",
                  month: "long"
                })}
                  </span>
                </div>
              </div>
            </button>

            {hydratedSelectedDestination && !hydratedPeek && <div className="hidden font-normal 3xs:flex">
                <div className="flex justify-end gap-x-0.5">
                  <StopMarker className={`size-4 ${stopType === "bus" ? "text-orange-400" : stopType === "train" ? "text-green-500" : stopType === "cable_car" ? "text-red-500" : "text-blue-500"}`} />
                  Depart
                </div>
                <div className="flex w-[clamp(2rem,5.5rem,5.5rem)] justify-end gap-x-0.5">
                  <DestinationFlag className="size-4" />
                  Arrive
                </div>
              </div>}
          </div>
        </div>}
      {/* Timetable sticky header refactored */}
      {isClient && isTimetable && (() => {
      const date_1 = clampToFeedRange(ttDate);
      const loopDate = new Date(`${date_1}T00:00:00`);
      return <div className="sticky top-12 z-10 bg-white dark:bg-black">
              <div className="px-[20px] py-10 pb-1.5 pt-3 capitalize leading-tight">
                {/* Row 1: arrows + heading (modal trigger) */}
                <div className="flex items-end gap-2">
                  <button type="button" onClick={() => setTtDate(d_3 => clampToFeedRange(addDays(d_3, -1)))} disabled={feedInfo?.start_date && clampToFeedRange(ttDate) <= feedInfo.start_date} className="pointer-events-auto relative grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none ring-blue-500/50 transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 disabled:pointer-events-none disabled:opacity-30 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800 dark:disabled:opacity-25" title="Previous day">
                    <ChevronLeft className="size-5" />
                  </button>

                  {/* Only this heading opens the view options modal */}
                  <button type="button" onClick={openViewModeModal} className="min-w-0 flex-1 select-none pl-1 text-left outline-none ring-blue-500/50 focus:outline-none focus-visible:ring" title="Switch view">
                    <div className="flex items-center text-sm font-semibold opacity-60">
                      Timetable
                      <ChevronDownSmall className="size-4" />
                    </div>
                    <div className="flex min-w-0 items-center gap-2 capitalize">
                      <div className="flex min-w-0">
                        {isClient && <div className="mr-1 inline-block shrink-0 font-bold">
                            {getRelativeDate(loopDate)}
                          </div>}
                        <div className="truncate opacity-60 @[12rem]:block">
                          {loopDate.toLocaleString("en-AU", {
                      weekday: "long",
                      day: "numeric",
                      month: "long"
                    })}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isTimetableToday && nowMarkerIndex !== null && <button type="button" onClick={jumpToNow} className="flex cursor-pointer items-center justify-center rounded-full px-4 py-2 text-center font-semibold text-blue-600 ring-1 ring-blue-500/50 transition-colors duration-200 hover:bg-blue-400/10 focus:outline-none focus-visible:ring dark:text-blue-400 dark:ring-blue-400" title="Jump to now">
                      Now
                    </button>}

                  <button type="button" onClick={() => setTtDate(d_4 => clampToFeedRange(addDays(d_4, +1)))} disabled={feedInfo?.end_date && clampToFeedRange(ttDate) >= feedInfo.end_date} className="pointer-events-auto relative grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none ring-blue-500/50 transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 disabled:pointer-events-none disabled:opacity-30 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800 dark:disabled:opacity-25" title="Next day">
                    <ChevronRight className="size-5" />
                  </button>
                </div>

                {/* Row 2: Depart/Arrive legend sits below arrows in timetable */}
                {hydratedSelectedDestination && !hydratedPeek && <div className="hidden justify-end pt-4 3xs:flex">
                    <div className="flex font-normal">
                      <div className="flex justify-end gap-x-0.5">
                        <StopMarker className={`size-4 ${stopType === "bus" ? "text-orange-400" : stopType === "train" ? "text-green-500" : stopType === "cable_car" ? "text-red-500" : "text-blue-500"}`} />
                        Depart
                      </div>
                      <div className="flex w-[clamp(2rem,5.5rem,5.5rem)] justify-end gap-x-0.5">
                        <DestinationFlag className="size-4" />
                        Arrive
                      </div>
                    </div>
                  </div>}
              </div>
            </div>;
    })()}
      {isLoading ? <div className="px-[20px] pt-4 text-lg opacity-40">Loading...</div> : departures?.length && <div className={`min-h-[1px] flex-1 transition-opacity ${isTimetable ? "pointer-events-auto opacity-100" : Date.now() - lastUpdated >= 300000 ? "pointer-events-none opacity-20" : "pointer-events-auto opacity-100"}`}>
            {isClient && (isTimetable ? <>
                  <Virtuoso ref={virtuosoRef} useWindowScroll increaseViewportBy={{
          top: 600,
          bottom: 600
        }} data={timetableData || []} itemContent={(index_0, item_0) => {
          if (!item_0) return null;
          if (item_0.__type === "now-marker") {
            const nowLabel = new Date(Date.now() + dateOffset).toLocaleTimeString("en-AU", {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
              timeZone: "Pacific/Auckland"
            });
            return <div className="px-[20px] py-2">
                            <div className="flex items-center">
                              <div className="rounded-full bg-blue-500 px-2 py-1 text-center text-xs font-semibold text-white">
                                Now{" "}
                                <span className="opacity-60"> {nowLabel}</span>
                              </div>

                              <div className="-ml-2 h-1 flex-1 rounded-full bg-blue-500" />
                            </div>
                          </div>;
          }
          const departure = item_0;
          const routeDetails = getRouteDetails(departure.route_id, servicesAtStop);
          const routeLabel = departure.route_short_name;
          return <Expected key={`${departure.trip_id}-${selectedDestination?.stop_id}-${index_0}`} routeLabel={routeLabel} trip_id={departure.trip_id} trip_headsign={departure.trip_headsign} stop_headsign={departure.stop_headsign} school={school_bus_schools.get(routeLabel)} expected={departure.expected_departure_time} cancelled={departure.cancelled} aimed={departure.scheduled_departure_time} seconds={NaN} stopType={stopType} stopId={stopId} bikes_allowed={departure.bikes_allowed} bus_replaced={departure.bus_replaced} route_color={routeDetails?.route_color || "#000"} route_type={routeDetails?.route_type} vehicle={departure.vehicle} destination_stop_scheduled_arrival_time={departure.destination_stop_scheduled_arrival_time} destination_stop_expected_arrival_time={departure.destination_stop_expected_arrival_time} platform={departure.platform} destination_stop_id={selectedDestination?.stop_id} destination_stop_name={selectedDestination?.stop_name} peek={peek} isTimetable nowMs={time.getTime()} showRecentDepartures={showRecentDepartures} />;
        }} />
                </> : <div>
                  {groupedDepartures?.map(([date_2, deps]) => {
          const loopDate_0 = new Date(`${date_2}T00:00:00`);
          return <section key={date_2}>
                        <div className="sticky top-12 z-10 bg-white dark:bg-black">
                          <div className="flex items-end justify-between gap-x-4 px-[20px] py-10 pb-1.5 pt-3 capitalize leading-tight">
                            <button type="button" onClick={openViewModeModal} className="group min-w-0 flex-1 select-none text-left outline-none" title="Switch view">
                              <div className="flex items-center gap-0.5 text-sm font-semibold opacity-60">
                                Departures
                                <ChevronDownSmall className="size-4" />
                              </div>
                              <div className="flex min-w-0 items-center gap-2">
                                <div className="flex w-full min-w-0 flex-wrap @container">
                                  {isClient && <span className="mr-1 inline-block truncate font-bold capitalize">
                                      {getRelativeDate(loopDate_0)}
                                    </span>}
                                  <span className="hidden truncate opacity-60 @[12rem]:inline">
                                    {loopDate_0.toLocaleString("en-AU", {
                          weekday: "long",
                          day: "numeric",
                          month: "long"
                        })}
                                  </span>
                                </div>
                              </div>
                            </button>

                            {hydratedSelectedDestination && !hydratedPeek && <div className="hidden font-normal 3xs:flex">
                                <div className="flex justify-end gap-x-0.5">
                                  <StopMarker className={`size-4 ${stopType === "bus" ? "text-orange-400" : stopType === "train" ? "text-green-500" : stopType === "cable_car" ? "text-red-500" : "text-blue-500"}`} />
                                  Depart
                                </div>
                                <div className="flex w-[clamp(2rem,5.5rem,5.5rem)] justify-end gap-x-0.5">
                                  <DestinationFlag className="size-4" />
                                  Arrive
                                </div>
                              </div>}
                          </div>
                        </div>

                        {deps.map((departure_0, index_1) => {
              const routeDetails_0 = getRouteDetails(departure_0.route_id, servicesAtStop);
              const seconds = (Date.parse(departure_0.expected_departure_time || departure_0.scheduled_departure_time) - time.getTime()) / 1000;
              const routeLabel_0 = departure_0.route_short_name;
              return <Expected key={`${departure_0.trip_id}-${selectedDestination?.stop_id}-${date_2}-${index_1}`} routeLabel={routeLabel_0} trip_id={departure_0.trip_id} trip_headsign={departure_0.trip_headsign} stop_headsign={departure_0.stop_headsign} school={school_bus_schools.get(routeLabel_0)} expected={departure_0.expected_departure_time} cancelled={departure_0.cancelled} aimed={departure_0.scheduled_departure_time} seconds={seconds} stopType={stopType} stopId={stopId} bikes_allowed={departure_0.bikes_allowed} bus_replaced={departure_0.bus_replaced} route_color={routeDetails_0?.route_color || "#000"} route_type={routeDetails_0?.route_type} vehicle={departure_0.vehicle} destination_stop_scheduled_arrival_time={departure_0.destination_stop_scheduled_arrival_time} destination_stop_expected_arrival_time={departure_0.destination_stop_expected_arrival_time} platform={departure_0.platform} destination_stop_id={selectedDestination?.stop_id} destination_stop_name={selectedDestination?.stop_name} peek={peek} isTimetable={false} nowMs={time.getTime()} showRecentDepartures={showRecentDepartures} />;
            })}
                      </section>;
        })}
                </div>)}
          </div>}
      {(isArrivalOnly || departures?.message) && <div className="flex flex-col gap-y-4 px-[20px] pt-4 text-lg">
          <p className="opacity-80">{departureEmptyState.message}</p>
          {departureEmptyState.showClearServicesAction && <>
              <p className="opacity-80">Try clearing your selected services.</p>
              <button className="flex w-full cursor-pointer items-center justify-center rounded-full px-6 py-2 text-center font-semibold text-blue-600 ring-1 ring-blue-500/50 transition-colors duration-200 hover:bg-blue-400/10 focus:outline-none focus-visible:ring dark:text-blue-400 dark:ring-blue-400" onClick={() => {
          setPreferredServicesTemp([]);
          savePreferredServices([]);
        }}>
                Clear selected services
              </button>
            </>}
        </div>}

      {(preferredServicesTemp?.length > 0 || hydratedSelectedDestination && servicesModalIsOpen === false && servicesAtStop.length > preferredServicesTemp?.length) && <div className="pointer-events-none fixed bottom-0 z-10 mx-auto flex w-full max-w-xl flex-row-reverse pb-[calc(1.25rem+env(safe-area-inset-bottom))] pr-5">
          <button className="pointer-events-auto mt-5 flex h-12 select-none flex-nowrap items-center justify-center rounded-full bg-blue-500 px-[20px] font-bold text-white shadow-lg outline-none ring-blue-500/50 transition-all duration-200 hover:bg-blue-600 focus-visible:ring active:bg-blue-600" onClick={() => setPeek(!peek)}>
            {hydratedPeek ? "Show Filtered" : "Show All"}
          </button>
        </div>}
    </PageWrapper>;
}
function _temp(service, index) {
  return {
    "@type": "ListItem",
    position: index + 1,
    name: getServiceDisplayName(service)
  };
}
function _temp2(nearbyStop, index_0) {
  return {
    "@type": "ListItem",
    position: index_0 + 1,
    url: `${siteUrl}/stop/${encodeURIComponent(nearbyStop.stop_id)}`,
    name: nearbyStop.stop_name
  };
}